import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Popover } from 'antd'
import * as weatherApi from './api'
import type { HeaderWeather } from './types'
import { formatTemp } from './utils'
import styles from './WeatherWidget.module.css'

export function WeatherWidget() {
  const navigate = useNavigate()
  const [data, setData] = useState<HeaderWeather | null>(null)
  const [open, setOpen] = useState(false)

  const load = useCallback(async () => {
    try {
      setData(await weatherApi.fetchHeaderWeather())
    } catch {
      setData(null)
    }
  }, [])

  useEffect(() => {
    void load()
    const timer = setInterval(() => void load(), 15 * 60 * 1000)
    return () => clearInterval(timer)
  }, [load])

  const current = data?.current
  const today = data?.today
  const emoji = current?.emoji || '☁️'
  const temp = formatTemp(current?.t_air_t)

  const content = (
    <div className={styles.popover}>
      <p className={styles.popoverTitle}>
        Сейчас
        {current?.dt ? ` (${current.dt}${current.tm ? `, ${current.tm}` : ''})` : ''}
      </p>
      <div className={styles.popoverBlock}>
        <div>
          {emoji} {current?.phenomenon_title_t || 'Нет данных'}
        </div>
        <div>🌡️ {temp}</div>
        <div>💧 Влажность: {current?.humidity_t ?? '—'}%</div>
        <div>📈 Давление: {current?.pressure_t ?? '—'} мм</div>
        <div>
          💨 Ветер: {current?.wind_direction_t || '—'}, {current?.wind_speed_t ?? '—'} м/с
        </div>
      </div>
      {today?.night && today?.day ? (
        <div className={styles.popoverBlock}>
          <p className={styles.popoverMuted}>Прогноз на {today.date || today.dt || 'сегодня'}</p>
          <div style={{ marginBottom: 8 }}>
            <div className={styles.popoverMuted}>🌙 Ночь</div>
            <div>{today.night.temp || '—'}</div>
            <div>
              {today.night.emoji} {today.night.desc}
            </div>
          </div>
          <div>
            <div className={styles.popoverMuted}>☀️ День</div>
            <div>{today.day.temp || '—'}</div>
            <div>
              {today.day.emoji} {today.day.desc}
            </div>
          </div>
        </div>
      ) : null}
      <Link to="/weather" className={styles.popoverLink} onClick={() => setOpen(false)}>
        Открыть страницу погоды
      </Link>
    </div>
  )

  return (
    <Popover
      content={content}
      trigger="hover"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
    >
      <button
        type="button"
        className={styles.pill}
        aria-label="Погода"
        title="Погода"
        onClick={() => navigate('/weather')}
      >
        <span className={styles.emoji} aria-hidden>
          {emoji}
        </span>
        <span className={styles.temp}>{temp}</span>
      </button>
    </Popover>
  )
}
